import { useState, useEffect } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";

const emptyIng = () => ({ name: "", quantity: "", unit: "kg", unit_cost: "" });
const title = (s) => s.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

export default function CreateRecipeDialog({ product, onClose, onCreated }) {
  const [inventory, setInventory] = useState([]);
  const [form, setForm] = useState({ name: "", selling_price: "", portions: 1, ingredients: [emptyIng()] });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/inventory").then(({ data }) => setInventory(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (product) setForm({ name: title(product.product), selling_price: product.qty ? (product.amount / product.qty).toFixed(2) : "", portions: 1, ingredients: [emptyIng()] });
  }, [product]);

  const setIng = (i, patch) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((x, k) => (k === i ? { ...x, ...patch } : x)) }));
  const pickIng = (i, name) => {
    const it = inventory.find((x) => x.name === name);
    setIng(i, { name, ...(it ? { unit: it.unit || "kg", unit_cost: it.unit_cost ?? "" } : {}) });
  };
  const cost = form.ingredients.reduce((s, x) => s + (parseFloat(x.quantity) || 0) * (parseFloat(x.unit_cost) || 0), 0);
  const price = parseFloat(form.selling_price) || 0;

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome ricetta obbligatorio");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), portions: parseFloat(form.portions) || 1, selling_price: price,
        ingredients: form.ingredients.filter((x) => x.name.trim()).map((x) => ({ name: x.name.trim(), quantity: parseFloat(x.quantity) || 0, unit: x.unit || "kg", unit_cost: parseFloat(x.unit_cost) || 0 })) };
      const { data } = await api.post("/food-costs", payload);
      await api.put("/sales/recipe-map", { product: product.product, recipe_id: data.id });
      toast.success(`Ricetta "${payload.name}" creata e abbinata a ${product.product}`);
      onCreated?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="create-recipe-dialog">
        <DialogHeader>
          <DialogTitle>Crea ricetta per "{product?.product}"</DialogTitle>
          <DialogDescription>Nome e prezzo sono precompilati dalla cassa. Aggiungi gli ingredienti dal magazzino: verranno scaricati ad ogni vendita.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5 sm:col-span-2"><Label>Nome ricetta</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cr-name" /></div>
          <div className="space-y-1.5"><Label>Prezzo di vendita €</Label><Input type="number" inputMode="decimal" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} data-testid="cr-price" /></div>
        </div>
        <datalist id="cr-inventory">{inventory.map((i) => <option key={i.id} value={i.name} />)}</datalist>
        <div className="space-y-2 mt-2">
          <Label>Ingredienti (per 1 porzione)</Label>
          {form.ingredients.map((x, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <Input list="cr-inventory" className="col-span-5" placeholder="Ingrediente dal magazzino" value={x.name} onChange={(e) => pickIng(i, e.target.value)} data-testid={`cr-ing-name-${i}`} />
              <Input type="number" inputMode="decimal" className="col-span-2" placeholder="Q.tà" value={x.quantity} onChange={(e) => setIng(i, { quantity: e.target.value })} data-testid={`cr-ing-qty-${i}`} />
              <Input className="col-span-2" placeholder="Unità" value={x.unit} onChange={(e) => setIng(i, { unit: e.target.value })} data-testid={`cr-ing-unit-${i}`} />
              <Input type="number" inputMode="decimal" className="col-span-2" placeholder="€/unità" value={x.unit_cost} onChange={(e) => setIng(i, { unit_cost: e.target.value })} data-testid={`cr-ing-cost-${i}`} />
              <Button size="icon" variant="ghost" className="col-span-1" onClick={() => setForm({ ...form, ingredients: form.ingredients.filter((_, k) => k !== i) })}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setForm({ ...form, ingredients: [...form.ingredients, emptyIng()] })} data-testid="cr-add-ing"><Plus className="w-4 h-4 mr-1" /> Aggiungi ingrediente</Button>
        </div>
        <DialogFooter className="items-center gap-3">
          <span className="text-sm text-muted-foreground mr-auto" data-testid="cr-summary">Costo porzione <b>€ {cost.toFixed(2)}</b>{price ? <> · Food cost <b>{((cost / price) * 100).toFixed(1)}%</b></> : null}</span>
          <Button onClick={save} disabled={busy} className="rounded-full" data-testid="cr-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea e abbina"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
